import { type Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { StripeLogger } from './logger';

/**
 * Loads the Stripe SDK with retry logic.
 * Attempts to load the Stripe SDK up to 3 times, with a delay of 500ms between attempts.
 * If an attempt does not return within 5 seconds, the promise will reject to be retried.
 */
export async function loadStripeWithRetry(publishableKey: string): Promise<Stripe | null> {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await withTimeout(loadStripe(publishableKey, STRIPE_LOAD_OPTIONS), TIMEOUT_MS);
    } catch (error) {
      StripeLogger.logStripeLoadError(error as Error);

      if (attempt < MAX_ATTEMPTS) {
        await sleep(RETRY_DELAY_MS);
      }
    }
  }

  return Promise.reject('Something went wrong while loading the payment provider.');
}

/**
 * If the promise does not resolve within the timeout, reject with the given message.
 * This can happen if `loadStripe` neither resolves nor rejects, leading to a stuck state.
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<T>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Timeout loading Stripe'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 500;
const TIMEOUT_MS = 5000;
const STRIPE_LOAD_OPTIONS = {
  developerTools: {
    assistant: {
      enabled: false,
    },
  },
};
